import { useMemo, useState, useEffect, type CSSProperties } from 'react';
import { diffLines } from 'diff';
import { useApiQuery } from '../use-api.js';
import { useAuth } from '../auth.js';
import { apiFetch, UnauthorizedError } from '../api-client.js';
import { useQuery } from '@tanstack/react-query';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';

interface PolicyVersionListItem {
  version: string;       // short_sha
  full_sha: string;
  pushed_at: string;
  author_login: string;
  commit_message: string;
  pr_number: number | null;
  pr_url: string | null;
  content_size: number;
  content_sha256: string;
}

export interface DefaultSelection {
  from: string | null;
  to: string | null;
}

export function defaultSelection(versions: PolicyVersionListItem[]): DefaultSelection {
  if (versions.length === 0) return { from: null, to: null };
  if (versions.length === 1) {
    const only = versions[0]!.version;
    return { from: only, to: only };
  }
  return { from: versions[1]!.version, to: versions[0]!.version };
}

export interface DecoratedPart {
  kind: 'added' | 'removed' | 'unchanged';
  value: string;
}

export function decorateDiffParts(parts: { added?: boolean; removed?: boolean; value: string }[]): DecoratedPart[] {
  return parts.map((p) => ({
    kind: p.added ? 'added' : p.removed ? 'removed' : 'unchanged',
    value: p.value,
  }));
}

const emptyStateBox: CSSProperties = {
  background: 'var(--loop-neutral-100)',
  border: '1px solid var(--loop-neutral-200)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-300)',
  textAlign: 'center',
};

const partStyle = (kind: DecoratedPart['kind']): CSSProperties => {
  if (kind === 'added') return { background: 'var(--loop-green-100)', color: 'var(--loop-green-700)', display: 'block' };
  if (kind === 'removed') return { background: 'var(--loop-red-100)', color: 'var(--loop-red-700)', display: 'block' };
  return { display: 'block' };
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

export function PolicyVersions() {
  const { idToken, refreshSession } = useAuth();
  const { data, isLoading, error } = useApiQuery<PolicyVersionListItem[]>(
    ['policy-versions'],
    '/api/policy-versions'
  );

  const versions = useMemo(() => data ?? [], [data]);
  // Explicit user picks. `null` means "not chosen yet", in which case the
  // selection falls back to defaultSelection() below. Derived rather than
  // seeded via an effect so the list's first render is already correct
  // instead of rendering unselected and cascading a second render.
  const [pickedFrom, setPickedFrom] = useState<string | null>(null);
  const [pickedTo, setPickedTo] = useState<string | null>(null);

  const def = useMemo(
    () => (versions.length > 0 ? defaultSelection(versions) : null),
    [versions]
  );
  const from = pickedFrom ?? def?.from ?? null;
  const to = pickedTo ?? def?.to ?? null;

  // Two parallel content fetches. apiFetch throws UnauthorizedError on 401;
  // mirror useApiQuery's auth pattern: idToken in queryKey (cache invalidates
  // on token refresh), don't retry UnauthorizedError, recover via refreshSession.
  const fromQ = useQuery({
    queryKey: ['policy-version-content', from, idToken],
    enabled: !!from && !!idToken,
    queryFn: async () => {
      const res = await apiFetch(`/api/policy-versions/${from}/content`, idToken!, {});
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
    retry: (failureCount, error) => !(error instanceof UnauthorizedError) && failureCount < 1,
  });
  const toQ = useQuery({
    queryKey: ['policy-version-content', to, idToken],
    enabled: !!to && !!idToken,
    queryFn: async () => {
      const res = await apiFetch(`/api/policy-versions/${to}/content`, idToken!, {});
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
    retry: (failureCount, error) => !(error instanceof UnauthorizedError) && failureCount < 1,
  });
  useEffect(() => {
    if (fromQ.error instanceof UnauthorizedError || toQ.error instanceof UnauthorizedError) {
      void refreshSession();
    }
  }, [fromQ.error, toQ.error, refreshSession]);

  const decorated = useMemo<DecoratedPart[]>(() => {
    if (!fromQ.data || !toQ.data) return [];
    return decorateDiffParts(diffLines(fromQ.data, toQ.data));
  }, [fromQ.data, toQ.data]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>
          Policies
        </h2>
        <ConceptHelpIcon slug="policy-version" />
      </div>

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && <p className="error">Error: {String(error)}</p>}

      {data && versions.length === 0 && (
        <div style={emptyStateBox}>
          <p style={{ fontWeight: 600 }}>No policy versions captured yet.</p>
          <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>
            The first push to <code>policies/central.yml</code> will land here.
          </p>
        </div>
      )}

      {versions.length > 0 && (
        <>
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
              <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                <tr>
                  {['From', 'To', 'Version', 'Pushed', 'Author', 'Message', 'PR'].map((h) => (
                    <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.version} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                    <td style={{ padding: '8px 16px' }}>
                      <input
                        type="radio"
                        name="from"
                        checked={from === v.version}
                        onChange={() => setPickedFrom(v.version)}
                        aria-label={`From ${v.version}`}
                      />
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <input
                        type="radio"
                        name="to"
                        checked={to === v.version}
                        onChange={() => setPickedTo(v.version)}
                        aria-label={`To ${v.version}`}
                      />
                    </td>
                    <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{v.version}</td>
                    <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)' }}>{fmtDate(v.pushed_at)}</td>
                    <td style={{ padding: '8px 16px' }}>{v.author_login}</td>
                    <td style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.commit_message}</td>
                    <td style={{ padding: '8px 16px' }}>
                      {v.pr_url ? (
                        <a href={v.pr_url} target="_blank" rel="noreferrer" style={{ color: 'var(--loop-blue-600)' }}>
                          #{v.pr_number}
                        </a>
                      ) : (
                        <span style={{ color: 'var(--loop-neutral-500)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 528, marginBottom: 8, color: 'var(--loop-neutral-1100)' }}>
              Diff: {from} → {to}
            </h3>
            {(fromQ.isLoading || toQ.isLoading) && (
              <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading content…</p>
            )}
            {(fromQ.error || toQ.error) && (
              <p className="error">Failed to load content.</p>
            )}
            {decorated.length > 0 && (
              <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
                <pre
                  style={{
                    margin: 0,
                    padding: 'var(--loop-space-200)',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre',
                  }}
                >
                  {decorated.map((p, i) => (
                    <span key={i} style={partStyle(p.kind)}>{p.value}</span>
                  ))}
                </pre>
              </div>
            )}
            {fromQ.data && toQ.data && decorated.length === 0 && (
              <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No changes.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
